import React, { useEffect, useMemo, useState } from 'react';
import { Flex, Grid, Skeleton, useMediaQuery } from '@chakra-ui/react';
import { Explorer } from '../Explorer/explorer';
import { useLocation } from 'react-router-dom';
import { Content } from '../../pages/HomePage/Featured/Content/content';
import { Game } from './Game/game';
import { useFeatureProvider } from '../../providers/Feature/featureProvider';
import { CHAKRA_MAX_WIDTH_MOBILE } from '../../utils/themer';
import { GamesState } from 'src/pages/Games/games';
import { Color } from 'src/theme/color';

export const Header = () => {
	const { pathname } = useLocation();
	const featured = useFeatureProvider();
	const [state, handleState] = useState<GamesState>(GamesState.LOADING);

	const isHeaderAsContent = pathname === '/' || pathname === '/games';

	const [isMobile] = useMediaQuery(CHAKRA_MAX_WIDTH_MOBILE);

	const gameList = useMemo(
		() =>
			featured.games.map(game => (
				<Game
					key={game.address}
					isBlack={game.address === '0x29790a7858015072f94c03e5761b98d7fe4de9a3' ? true : false}
					data={game}
					setAsActive={featured.setFeatured}
					isActive={game.address === featured.current.game.address}
				/>
			)),
		[featured.games, featured.current]
	);

	useEffect(() => {
		if (featured.isLoading) {
			handleState(GamesState.LOADING);
		} else {
			handleState(GamesState.FETCHED);
		}
	}, [featured.isLoading]);

	const typeOptions: { [p: number]: () => React.ReactNode } = {
		[GamesState.LOADING]: () => (
			<Flex
				justifyContent="space-between"
				w="100%"
				h="100%"
			>
				{Array.from(Array(5).keys()).map(id => {
					return (
						<Skeleton
							key={id}
							h="9vw"
							w="16vw"
						/>
					);
				})}
			</Flex>
		),
		[GamesState.FETCHED]: () => gameList,
	};

	const connectType = useMemo(() => {
		if (state === GamesState.LOADING) return GamesState.LOADING;
		else if (state === GamesState.FETCHED) return GamesState.FETCHED;
		return GamesState.LOADING;
	}, [state]);

	return (
		<Flex
			bg="black"
			position="relative"
			_before={{
				content: '""',
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				height: '100%',
				backgroundImage: `url(${featured.current.image})`,
				backgroundSize: 'cover',
				backgroundRepeat: 'no-repeat',
			}}
			width="100%"
			justifyContent="center"
			alignItems="center"
			pt={isHeaderAsContent ? (!isMobile ? '20px' : '30px') : '0'}
		>
			<Flex
				position="relative"
				paddingY={{ base: '0', hashup: '6' }}
				minWidth={{ base: 'unset', hashup: 'auto', xl: '1240px' }}
				maxWidth={{ base: 'unset', hashup: '1168px', xl: '1240px' }}
				flexDirection={{ base: 'column', hashup: 'row' }}
				paddingX={{ base: '16px', hashup: '32px', xl: '0' }}
				width={{ base: '100%', hashup: 'unset' }}
			>
				<Flex
					flexDirection="column"
					justify="center"
					w="100%"
					pb="12"
				>
					<Flex
						justify="space-between"
						userSelect="none"
						flexDirection={{ base: 'column-reverse', hashup: 'row' }}
					>
						<Flex
							ml={{ base: '0', hashup: '12' }}
							mt={{ base: '32', hashup: '16' }}
							minH={{ base: 'unset', hashup: '396px' }}
							pb={{ base: '8', hashup: '0' }}
						>
							<Content
								data={featured.current}
								isLoading={featured.isLoading}
							/>
						</Flex>
						<Explorer />
					</Flex>
					<Grid
						display={{ base: 'grid', hashup: 'flex' }}
						overflowX={{ base: 'scroll', hashup: 'unset' }}
						width="100%"
						css={{
							'&::-webkit-scrollbar': {
								width: '0 !important',
							},
							overflow: '-moz-scrollbars-none',
							msOverflowStyle: 'none',
						}}
					>
						<Flex
							flexWrap="nowrap"
							width={{ base: 'calc(200vw - 32px)', hashup: '100%' }}
							gap="12px"
							margin={{ base: '0 auto', hashup: '0' }}
							overflowX={{ base: 'scroll', hashup: 'unset' }}
							css={{
								'&::-webkit-scrollbar': {
									width: '0 !important',
								},
								overflow: '-moz-scrollbars-none',
								msOverflowStyle: 'none',
							}}
						>
							{useMemo(() => typeOptions[connectType](), [state, featured.current])}
						</Flex>
					</Grid>
				</Flex>
			</Flex>
		</Flex>
	);
};
